import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IMeetingEmailTemplate } from '../../Interfaces/IMeetingEmailTemplate';
import { customFetcher } from '../useFetch';

export const useGetMeetingEmailTemplates = () => {
  const {
    data: meetingEmailTemplates,
    isLoading: isLoadingMeetingEmailTemplates,
    refetch: refetchMeetingEmailTemplates,
  } = useQuery<IMeetingEmailTemplate[]>({
    refetchOnWindowFocus: false,
    queryKey: ['meetingemailtemplate'],
    queryFn: () => customFetcher({ endpoint: '/meetingemailtemplate' }),
  });

  return {
    meetingEmailTemplates,
    isLoadingMeetingEmailTemplates,
    refetchMeetingEmailTemplates,
  };
};

export const useCreateMeetingEmailTemplate = () => {
  const qc = useQueryClient();
  const {
    mutateAsync: createMeetingEmailTemplate,
    isPending: isPendingCreateMeetingEmailTemplate,
  } = useMutation({
    mutationFn: (
      template: Omit<IMeetingEmailTemplate, 'idMeetingEmailTemplate'>
    ) =>
      customFetcher({
        endpoint: '/meetingemailtemplate', // oder '/meetingemailtemplate' je nach Proxy
        method: 'POST',
        body: template,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['meetingemailtemplate'] });
    },
  });

  return { createMeetingEmailTemplate, isPendingCreateMeetingEmailTemplate };
};

export const useUpdateMeetingEmailTemplate = () => {
  const qc = useQueryClient();
  const {
    mutateAsync: updateMeetingEmailTemplate,
    isPending: isPendingUpdateMeetingEmailTemplate,
  } = useMutation({
    mutationFn: (template: IMeetingEmailTemplate) =>
      customFetcher({
        endpoint: '//meetingemailtemplate/fullObject', // gleiche URL wie POST
        method: 'PUT',
        body: template,
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['meetingemailtemplate'] });
    },
  });

  return { updateMeetingEmailTemplate, isPendingUpdateMeetingEmailTemplate };
};

export const useDeleteMeetingEmailTemplate = () => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: deleteMeetingEmailTemplate,
    isPending: isPendingDeleteMeetingEmailTemplate,
  } = useMutation({
    mutationFn: (id: number) =>
      customFetcher({
        endpoint: `/meetingemailtemplate/${id}`,
        method: 'DELETE',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['meetingemailtemplate'],
      });
    },
  });

  return { deleteMeetingEmailTemplate, isPendingDeleteMeetingEmailTemplate };
};
